import { EVENT_DELAY } from '@src/config/constants'
import { CONFIG_KEY_FB_PIXEL_ID } from '@src/config/env'
import { getLocaleConfigFromMemory } from '@src/modules/global-state/locale-config.browser'
import { ANALYTIC_COOKIES } from '@src/quince-site-common/src/utils/constants'
import getCookie from '@src/quince-site-common/src/utils/getCookie'
import { getlbUserId } from '@src/quince-site-common/src/utils/getlb-user-id'
import { initializeScript } from '@src/telemetry/integrations/provider-utils/initialize-script'
import { PARTYTOWN_SCRIPT_TYPE, dispatchPartytownUpdate } from '@src/telemetry/partytown/partytown-config'
import {
  getPartytownConfigFromBrowser,
  isPartytownEnabledInBrowser,
} from '@src/telemetry/partytown/partytown-config.browser'
import { trackRumAction } from '@src/telemetry/rum.helper'
import { TelemetryIntegration } from '@src/telemetry/telemetry-events.types'
import type { FBQ } from '@src/telemetry/telemetry-pixels.types'
import { getStorefrontIdProperty } from '@src/utils/b2b'
import { executeFireAndForgotTask } from '@src/utils/fire-and-forgot-task'
import { getLocalisationDataFromStorage } from '@src/utils/general.utils'

import { TELEMETRY_DOMAINS } from '../telemetry-domains.constants'
import { TELEMETRY_EVENT_NAMES } from '../telemetry-events.constants'

// Additional Facebook Pixel IDs to fire alongside the primary CONFIG_KEY_FB_PIXEL_ID.
// Order is preserved: each pixel is `init`-ed here and then receives a `trackSingle` PageView
// (with the shared event id) from the PAGE_VIEW event handler.
const ADDITIONAL_FB_PIXEL_IDS = [
  '1900161340840474', // Blotout V2
  '458941500243684', // Forked V4
] as const

// Idempotency guard for the Partytown path. In the non-Partytown path the
// `if (window.fbq) return` check below prevents re-init, but with Partytown's
// `forward: ['fbq']` a main-thread `window.fbq` stub always exists, so that
// check can't distinguish a fresh init from a repeat one.
let hasInitializedFbPixelViaPartytown = false

// Fires AFTER Partytown's own fallbackTimeout so the two never race: once that
// timeout has fired, Partytown permanently tears the sandbox down (and deletes
// the forward stub), so going main-thread here cannot double-execute.
//
// Computed per call, NOT at module scope: both terms come from the Statsig-resolved
// `ssr-partytown` config, which `_app` seeds into browser memory after this module is
// imported. Reading it at import time would always capture the pre-seed defaults and
// could leave the watchdog firing before the fallbackTimeout the server actually emitted.
const getPartytownPixelWatchdogMs = (): number => {
  const { fallbackTimeoutMs, pixelWatchdogGraceMs } = getPartytownConfigFromBrowser()
  return fallbackTimeoutMs + pixelWatchdogGraceMs
}

const getUserTrackingData = () => {
  return {
    em:
      getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_EMAIL)?.trim() ??
      getCookie(ANALYTIC_COOKIES.LB_CUSTOMER_TENTATIVE_EMAIL)?.trim() ??
      '',
    external_id: getlbUserId()?.trim() ?? '',
  }
}

// Per-locale gate for the secondary Blotout/Forked pixels (see `additionalFbPixelsAllowed`
// in StatsigLocaleDynamicConfig). Default true for US/CA, false for UK — only the primary
// CONFIG_KEY_FB_PIXEL_ID fires for UK. `?? true` keeps legacy behaviour when the flag is
// absent (e.g. unmocked tests fall back to en-US default which is true).
const getAllFbPixelIds = (): string[] => {
  const additionalFbPixelsAllowed = getLocaleConfigFromMemory().additionalFbPixelsAllowed ?? true
  return additionalFbPixelsAllowed ? [CONFIG_KEY_FB_PIXEL_ID, ...ADDITIONAL_FB_PIXEL_IDS] : [CONFIG_KEY_FB_PIXEL_ID]
}

// Partytown path: inject the standard Meta bootstrap as a single `text/partytown`
// script so the fbq stub, the fbevents.js load, and the pixel `init`s ALL run inside
// the worker (fbevents' bootstrap mutates the stub it creates, so it must exist in the
// same realm). PageView is NOT fired here — the PAGE_VIEW telemetry handler fires it
// (carrying the shared dedup event id), and Partytown's `forward: ['fbq']` relays that
// main-thread fbq call into the worker. `autoConfig:true` keeps Meta automatic events
// (SubscribedButtonClick) and microdata enrichment — listeners run in the worker, not on
// the main thread. `resolveUrl` in _document rewrites the connect.facebook.net fetch to
// the same-origin /fb-proxy. The `ptupdate` dispatch makes Partytown scan this
// dynamically-injected script.
const injectFbPixelViaPartytown = (
  pixelIds: readonly string[],
  userData: ReturnType<typeof getUserTrackingData>,
): HTMLScriptElement => {
  const data = JSON.stringify(userData)
  // `autoConfig` is Statsig-controlled (`ssr-partytown.fbPixelAutoConfig`, default true). It applies to
  // this worker path only — the main-thread path never sets it and Meta defaults it on — so switching it
  // off changes which automatic events the Partytown bucket reports, not just where scripts execute.
  const { fbPixelAutoConfig } = getPartytownConfigFromBrowser()
  const setAutoConfig = pixelIds
    .map(id => `fbq('set','autoConfig',${String(fbPixelAutoConfig)},${JSON.stringify(id)});`)
    .join('')
  const init = pixelIds.map(id => `fbq('init',${JSON.stringify(id)},${data});`).join('')

  const script = document.createElement('script')
  script.type = PARTYTOWN_SCRIPT_TYPE
  const nonce = window.__CSP_NONCE__
  if (nonce) script.nonce = nonce
  script.textContent =
    `!function(f,b,e,v,n,t,s){if(f.fbq)return;n=f.fbq=function(){n.callMethod?n.callMethod.apply(n,arguments):n.queue.push(arguments)};if(!f._fbq)f._fbq=n;n.push=n;n.loaded=!0;n.version='2.0';n.queue=[];t=b.createElement(e);t.async=!0;t.src=v;s=b.getElementsByTagName(e)[0];s.parentNode.insertBefore(t,s)}(window,document,'script','${TELEMETRY_DOMAINS.FB_PIXEL}/en_US/fbevents.js');` +
    setAutoConfig +
    init
  document.body.appendChild(script)
  dispatchPartytownUpdate()
  return script
}

// Fail-open watchdog for the Partytown path. The sandbox boot is not reliable:
// `partytown-sandbox-sw.html` is a virtual URL served by the Partytown service
// worker's fetch handler, and on cold visits the sandbox iframe request can race
// SW activation, miss interception, and 404 — the worker then never reads any
// script. Partytown's own fallbackTimeout doesn't cover us either: it replays
// only the `text/partytown` scripts present at DOMContentLoaded, and this
// bootstrap is injected later (deferred telemetry init) — measured result was
// total silent FB event loss for the document. When the worker hasn't READ the
// script (no `data-ptid`) after Partytown's fallback deadline has passed, give
// up on the worker and initialise fbevents on the main thread: events arrive a
// few seconds late and the INP win is lost for this document, but nothing is
// dropped.
const armPartytownPixelWatchdog = (script: HTMLScriptElement, pixelIds: readonly string[]) => {
  setTimeout(() => {
    const workerPickedItUp = script.type !== PARTYTOWN_SCRIPT_TYPE || Boolean(script.dataset.ptid)
    if (workerPickedItUp) return

    // Unread scripts are discovered via DOM query, so removing the node also
    // prevents a late-booting worker from double-executing the bootstrap.
    script.remove()

    // Clear Partytown's forward stub (a plain relay fn without `callMethod`) so
    // the main-thread bootstrap below doesn't early-return on `if (f.fbq)`.
    // Partytown's own fallback may have deleted it already; never touch a real
    // fbevents global (it has `callMethod`).
    const maybeForwardStub = window.fbq as FBQ | undefined
    if (maybeForwardStub && !maybeForwardStub.callMethod) {
      delete window.fbq
      delete window._fbq
    }

    trackRumAction('partytown_fb_pixel_fallback', { pixelIds: [...pixelIds] })
    void initializeFbPixelOnMainThread(pixelIds)
  }, getPartytownPixelWatchdogMs())
}

// Non-Partytown path (also the watchdog's fallback): load fbevents.js on the
// main thread and initialise directly.
const initializeFbPixelOnMainThread = async (allPixelIds: readonly string[]) => {
  await initializeScript({
    key: 'tracking-provider-fb',
    domain: TELEMETRY_DOMAINS.FB_PIXEL,
    path: '/en_US/fbevents.js',
    injectAt: 'beforeFirstScript',
    maxDelay: 1000,
  })

  if (window.fbq) return
  window.fbq = function (...args) {
    if (window.fbq?.callMethod) {
      window.fbq.callMethod(...args)
    } else {
      window.fbq?.queue?.push(args)
    }
  }
  const fbq = window.fbq
  if (!window._fbq) window._fbq = fbq
  fbq.push = fbq
  fbq.loaded = !0
  fbq.version = '2.0'
  fbq.queue = []

  // PageView is NOT fired here — it comes from the PAGE_VIEW telemetry handler
  // (carrying the shared dedup event id). `initialize` only sets up fbq + `init`s.
  allPixelIds.forEach(pixelId => {
    fbq('init', pixelId, getUserTrackingData())
  })
}

export const FbPixel: TelemetryIntegration = {
  // PageView is fired from the PAGE_VIEW event handler (not here) so it carries the shared
  // `properties.eventId` and deduplicates against the CAPI/SGTM PageView. `initialize` only
  // sets up fbq + `init`s each pixel.
  //  https://developers.facebook.com/ads/blog/post/v2/2017/05/29/tagging-a-single-page-application-facebook-pixel/
  config: {
    enabled: true,
    eventIdealTimeout: 1000,
    domain: TELEMETRY_DOMAINS.FB_PIXEL,
    featureGateName: 'ssr_telemetry_fb_pixel',
  },

  initialize: async () => {
    const usePartytown = isPartytownEnabledInBrowser()

    const allPixelIds = getAllFbPixelIds()

    if (usePartytown) {
      // Everything (stub + fbevents.js + pixel inits) must run inside the Partytown
      // worker in one script: fbevents' bootstrap sets properties on the `fbq` stub
      // it creates, so loading fbevents.js without the stub present in the same
      // realm throws (`Cannot set properties of undefined (setting 'version')`).
      // Guard against double-init — the forward stub always exists on the main
      // thread, so the non-Partytown `if (window.fbq) return` check can't be used.
      if (hasInitializedFbPixelViaPartytown) return
      hasInitializedFbPixelViaPartytown = true
      const script = injectFbPixelViaPartytown(allPixelIds, getUserTrackingData())
      armPartytownPixelWatchdog(script, allPixelIds)
      return
    }

    await initializeFbPixelOnMainThread(allPixelIds)
  },

  events: {
    [TELEMETRY_EVENT_NAMES.PAGE_VIEW]: properties => {
      logFbPixelPageView(properties.eventId)
    },
    [TELEMETRY_EVENT_NAMES.LEAD]: properties => {
      logFbPixelEvent({
        eventType: 'Lead',
        contentIds: [],
        contentType: 'lead',
        contentName: properties.contentName,
        contentCategory: '',
        value: 0,
      })
    },
    [TELEMETRY_EVENT_NAMES.ADD_TO_CART]: properties => {
      const value = properties?.value ?? properties?.price

      logFbPixelEvent({
        eventType: 'AddToCart',
        contentIds: [properties.variantId],
        contentType: 'product',
        contentName:
          properties.productTitle && properties.variantTitle
            ? `${String(properties.productTitle)} - ${String(properties.variantTitle)}`
            : '',
        contentCategory: properties.productType,
        value: value ? formatRevenue(Number(value)) : 0,
        numItems: properties.quantity || 1,
        eventId: (properties as { eventId?: string }).eventId,
      })
    },

    [TELEMETRY_EVENT_NAMES.PRODUCT_VIEW](properties) {
      const isPartnerProductPDPPage = properties.isPartnerProductPDPPage
      const isSingleVariant = properties.variantIds.length === 1
      if (!isPartnerProductPDPPage && properties.productTitle) {
        if (isSingleVariant) {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: properties.variantIds.length > 0 && properties.variantIds[0] ? [properties.variantIds[0]] : [],
            contentType: 'product',
            contentName:
              properties.productTitle && properties.variantTitle
                ? properties.productTitle + ' - ' + properties.variantTitle
                : '',
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
            eventId: properties.eventId,
          })
        } else {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: [properties.productId.toString()],
            contentType: 'product_group',
            contentName: properties.productTitle,
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
            eventId: properties.eventId,
          })
        }
      }
    },

    [TELEMETRY_EVENT_NAMES.VARIANT_SELECT]: properties => {
      const isSingleVariant = properties.variantIds.length === 1
      if (!properties.is_product_card && properties.productTitle) {
        if (isSingleVariant && properties.variantIds.length > 0 && properties.variantIds[0]) {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: [properties.variantIds[0]],
            contentType: 'product',
            contentName: properties.productTitle + ' - ' + properties.variantTitle,
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
            eventId: properties.eventId,
          })
        } else {
          logFbPixelEvent({
            eventType: 'ViewContent',
            contentIds: [properties.spid],
            contentType: 'product_group',
            contentName: properties.productTitle,
            contentCategory: properties.productType,
            value: formatRevenue(Number(properties.price)),
            eventId: properties.eventId,
          })
        }
      }
    },
  },
}

const logFbPixelEvent = ({
  eventType,
  contentIds,
  contentType,
  contentName,
  contentCategory,
  value,
  numItems,
  eventId,
}: {
  eventType: string
  contentIds: string[]
  contentType: string
  contentName: string
  contentCategory: string
  value: number
  numItems?: number
  eventId?: string
}) => {
  const localisationData = getLocalisationDataFromStorage()
  executeFireAndForgotTask(() => {
    if (window.fbq) {
      const fbEventData = {
        content_ids: contentIds,
        content_type: contentType,
        content_name: contentName,
        content_category: contentCategory,
        currency: localisationData?.currency ?? 'USD',
        country: localisationData?.country,
        value: value,
        num_items: numItems,
        event: eventType,
        ...getStorefrontIdProperty(), //storefront_id is used to identify the b2b self serve flow
      }
      // Meta dedup id must ride in the 4th options arg (fbevents `eid`), not the
      // data object — in the data object it only lands in cd[eventID] and never
      // deduplicates against the CAPI/SGTM event_id. Omit the 4th arg entirely
      // when there is no id so we never pass a trailing undefined.
      if (eventId) {
        window.fbq('track', eventType, fbEventData, { eventID: eventId })
      } else {
        window.fbq('track', eventType, fbEventData)
      }
    }
  }, EVENT_DELAY)
}

// Fire PageView per pixel via `trackSingle` (each pixel individually, avoiding the race where
// secondary pixels miss the event) carrying the shared `eventId` in the 4th options arg so it
// deduplicates against the CAPI/SGTM PageView. Omit the options arg when there is no id so we
// never pass a trailing undefined.
const logFbPixelPageView = (eventId?: string) => {
  executeFireAndForgotTask(() => {
    const fbq = window.fbq
    if (!fbq) return
    getAllFbPixelIds().forEach(pixelId => {
      if (eventId) {
        fbq('trackSingle', pixelId, 'PageView', {}, { eventID: eventId })
      } else {
        fbq('trackSingle', pixelId, 'PageView')
      }
    })
  }, EVENT_DELAY)
}

export const formatRevenue = (price: number | null) => {
  if (price != null) {
    return price
  }
  return 0
}
